import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { Subject, takeUntil } from 'rxjs';
import { SkeletonLoaderComponent } from '../../shared/skeleton-loader/skeleton-loader.component';
import {
  PointLeaderboardService,
  RatingCategoryBreakdown,
  RatingMatchLevelGroup,
  RatingTournamentBreakdownResponse,
} from '../../../utilities/services/point-leaderboard/point-leaderboard.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TemplateService } from '../../../utilities/services/template/template.service';

/** Summary row for tournament-level details or per-category drill-down. */
interface ComponentSummary {
  heading: string;
  labelKey: string;
  points: number;
  categoryId?: number;
  isTournamentWeight: boolean;
}

/** Tournament summary object from leaderboard-breakdown → event.details. */
interface EventBreakdownDetails {
  victory_points?: number;
  bonus_points?: number;
  penalty_points?: number;
  manual_vp_adjustment?: number;
  manual_bp_adjustment?: number;
  tournament_weight?: number;
}

/** Tournament row in the player breakdown list. */
interface EventPerformance {
  event_id: number;
  event_name: string;
  points: number;
  allocation_date: string;
  is_highlighted: boolean;
  details: EventBreakdownDetails | null;
  is_held?: boolean;
}

const HOLD_TIP_WIDTH = 180;
const HOLD_TIP_MARGIN = 12;

/**
 * Sidebar showing per-tournament rating point breakdown for a selected player.
 * Level 1 (pencil): event.details from leaderboard-breakdown.
 * Level 2 (point row click): category detail cards from tournament breakdown API.
 * Level 3 (category card click): match-level breakdown in a nested sidebar.
 */
@Component({
  selector: 'stupa-breadown-sidebar',
  standalone: true,
  imports: [
    SkeletonLoaderComponent,
    CommonModule,
    TranslateModule,
  ],
  templateUrl: './breakdown-sidebar.component.html',
  styleUrl: './breakdown-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Template 1 (OTT) skin — see `:host(.t1-breakdown)` in the stylesheet.
  host: { '[class.t1-breakdown]': 'isTemplate1' },
  animations: [
    trigger('breakdownAnimation', [
      state('in', style({ height: '*', opacity: 1, 'padding-top': '10px', 'padding-bottom': '10px' })),
      transition('void => *', [
        style({ height: 0, opacity: 0, padding: 0 }),
        animate('200ms ease-in-out'),
      ]),
      transition('* => void', [
        animate('200ms ease-in-out', style({ height: 0, opacity: 0, padding: 0 })),
      ]),
    ]),
  ],
})
export class BreakdownSidebarComponent implements OnInit, OnChanges, OnDestroy {
  @Input() playerData: { player_id: number; player?: string } | null = null;
  @Input() appliedFilters: { period?: { value: string }; season_id?: number; category_id?: number; event_ids?: number[] } | null = null;
  @Output() panelClose = new EventEmitter<void>();

  private readonly leaderboardService = inject(PointLeaderboardService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly templateService = inject(TemplateService);
  private readonly unsubscribe$ = new Subject<void>();

  participantName = 'Player Name';
  eventPerformances: EventPerformance[] = [];
  selectedBreakdownEventId: number | null = null;
  isLoading = false;
  isBreakdownLoading = false;
  isMatchLoading = false;

  /** Level 1 rows from event.details (shown on pencil click). */
  mappedBreakdownDetails: ComponentSummary[] = [];
  /** Level 2 category cards from tournament API (shown in nested sidebar). */
  categoryBreakdowns: RatingCategoryBreakdown[] = [];
  /** Level 3 match groups from match-level API (shown in nested sidebar). */
  matchBreakdownGroups: RatingMatchLevelGroup[] = [];
  selectedPointKey: string | null = null;
  selectedEventIdForCategory: number | null = null;
  selectedCategoryIdForMatch: number | null = null;
  selectedCategoryRowKey: string | null = null;
  tournamentNameForMatch: string | null = null;
  isCategorySidebarOpen = false;
  isMatchSidebarOpen = false;
  holdTip: { text: string; top: number; left: number } | null = null;
  /** True when the OTT layout (template 1) is set via `environment.template`. */
  readonly isTemplate1 = this.templateService.isTemplate1;

  private readonly tournamentBreakdownCache = new Map<number, RatingTournamentBreakdownResponse>();
  private readonly matchBreakdownCache = new Map<string, RatingMatchLevelGroup[]>();
  private scrollLockStyles: {
    bodyOverflow: string;
    htmlOverflow: string;
  } | null = null;

  ngOnInit(): void {
    this.lockBodyScroll();
    if (this.playerData) {
      this.participantName = this.playerData.player ?? this.participantName;
      this.fetchBreakdown();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['playerData']) {
      this.fetchBreakdown();
    }
  }

  ngOnDestroy(): void {
    this.unlockBodyScroll();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /** Prevents background page scroll while any breakdown sidebar level is open. */
  private lockBodyScroll(): void {
    const html = document.documentElement;
    const body = document.body;
    this.scrollLockStyles = {
      bodyOverflow: body.style.overflow,
      htmlOverflow: html.style.overflow,
    };
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
  }

  /** Restores page scroll after the breakdown sidebar is closed or destroyed. */
  private unlockBodyScroll(): void {
    if (!this.scrollLockStyles) {
      return;
    }
    document.body.style.overflow = this.scrollLockStyles.bodyOverflow;
    document.documentElement.style.overflow = this.scrollLockStyles.htmlOverflow;
    this.scrollLockStyles = null;
  }

  /** Loads the top-level tournament list for the selected player. */
  fetchBreakdown(): void {
    if (!this.playerData?.player_id) {
      return;
    }

    this.isLoading = true;
    this.tournamentBreakdownCache.clear();
    this.matchBreakdownCache.clear();
    const currentDate = new Date().toISOString().split('T')[0];

    this.leaderboardService
      .getRatingBreakdown(this.playerData.player_id, currentDate)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          
          this.participantName = response.player_name || this.participantName;
          this.eventPerformances = (response.breakdown || []).map((event: {
            event_id: number;
            event_name: string;
            total_points: number;
            allocation_date: string;
            details?: EventBreakdownDetails;
            is_held?: boolean;
          }) => ({
            event_id: event.event_id,
            event_name: event.event_name,
            points: event.total_points,
            allocation_date: new Date(event.allocation_date).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            }),
            is_highlighted: false,
            details: event.details ?? null,
            is_held: !!event.is_held,
          }));
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.snackBar.open(
            this.translate.instant('rating_breakdown_failed_to_load'),
            this.translate.instant('close'),
            { duration: 3000 },
          );
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Expands tournament breakdown using event.details from the initial API.
   * @param eventId - Tournament identifier
   */
  toggleBreakdown(eventId: number): void {
    if (this.selectedBreakdownEventId === eventId) {
      this.resetBreakdownState();
      return;
    }

    this.selectedBreakdownEventId = eventId;
    this.closeCategorySidebar();
    this.isBreakdownLoading = false;

    const selectedEvent = this.eventPerformances.find((event) => event.event_id === eventId);
    this.mappedBreakdownDetails = selectedEvent?.details
      ? this.mapDetailsToDisplay(selectedEvent.details, !!selectedEvent.is_held)
      : [];

    this.cdr.markForCheck();
  }

  /**
   * Opens the nested category sidebar and loads tournament breakdown data.
   * @param labelKey - Translation key for the selected point type
   * @param eventId - Tournament identifier
   */
  onPointClick(labelKey: string, eventId: number): void {
    if (!this.playerData?.player_id) {
      return;
    }

    this.selectedPointKey = labelKey;
    this.selectedEventIdForCategory = eventId;
    this.categoryBreakdowns = [];
    this.closeMatchSidebar();
    this.isCategorySidebarOpen = true;

    const cached = this.tournamentBreakdownCache.get(eventId);
    if (cached) {
      this.categoryBreakdowns = cached.category_breakdowns ?? [];
      this.cdr.markForCheck();
      return;
    }

    this.isBreakdownLoading = true;
    this.cdr.markForCheck();

    this.leaderboardService
      .getRatingPlayerTournamentBreakdown(this.playerData.player_id, eventId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          this.tournamentBreakdownCache.set(eventId, response);
          this.categoryBreakdowns = response.category_breakdowns ?? [];
          this.isBreakdownLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.snackBar.open(
            this.translate.instant('rating_tournament_breakdown_failed_to_load'),
            this.translate.instant('close'),
            { duration: 3000 },
          );
          this.isBreakdownLoading = false;
          this.closeCategorySidebar();
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Whether a category detail row is the active drill-down selection.
   * @param categoryId - Category identifier
   * @param rowKey - Row identifier (victory_points, bonus_points, penalty_points)
   */
  isCategoryRowActive(categoryId: number, rowKey: string): boolean {
    return (
      this.isMatchSidebarOpen &&
      this.selectedCategoryIdForMatch === categoryId &&
      this.selectedCategoryRowKey === rowKey
    );
  }

  /**
   * Opens the match-level sidebar when a category detail row is clicked.
   * @param category - Selected category from the tournament breakdown API
   * @param rowKey - Clicked row identifier for active-state highlighting
   * @param event - Click event used to stop propagation
   */
  onCategoryDetailRowClick(
    category: RatingCategoryBreakdown,
    rowKey: string,
    event: Event,
  ): void {
    event.stopPropagation();
    if (!this.playerData?.player_id || this.selectedEventIdForCategory == null) {
      return;
    }

    this.selectedCategoryIdForMatch = category.category_id;
    this.selectedCategoryRowKey = rowKey;
    this.matchBreakdownGroups = [];
    this.tournamentNameForMatch = null;
    this.isMatchSidebarOpen = true;

    const cacheKey = `${this.selectedEventIdForCategory}-${category.category_id}`;
    const cached = this.matchBreakdownCache.get(cacheKey);
    if (cached) {
      this.matchBreakdownGroups = cached;
      this.cdr.markForCheck();
      return;
    }

    this.isMatchLoading = true;
    this.cdr.markForCheck();

    this.leaderboardService
      .getRatingPlayerMatchLevelBreakdown(
        this.playerData.player_id,
        this.selectedEventIdForCategory,
        category.category_id,
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          this.tournamentNameForMatch = response.tournament_name;
          this.matchBreakdownGroups = response.rating_breakdown ?? [];
          this.matchBreakdownCache.set(cacheKey, this.matchBreakdownGroups);
          this.isMatchLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.snackBar.open(
            this.translate.instant('rating_match_breakdown_failed_to_load'),
            this.translate.instant('close'),
            { duration: 3000 },
          );
          this.isMatchLoading = false;
          this.closeMatchSidebar();
          this.cdr.markForCheck();
        },
      });
  }

  /** Closes the match-level nested sidebar. */
  closeMatchSidebar(): void {
    this.isMatchSidebarOpen = false;
    this.matchBreakdownGroups = [];
    this.selectedCategoryIdForMatch = null;
    this.selectedCategoryRowKey = null;
    this.tournamentNameForMatch = null;
    this.isMatchLoading = false;
    this.cdr.markForCheck();
  }

  /** Closes the nested category breakdown sidebar and returns to the main view. */
  closeCategorySidebar(): void {
    this.closeMatchSidebar();
    this.isCategorySidebarOpen = false;
    this.categoryBreakdowns = [];
    this.selectedPointKey = null;
    this.selectedEventIdForCategory = null;
    this.isBreakdownLoading = false;
    this.cdr.markForCheck();
  }

  /**
   * Formats a match date for display, or N/A when absent.
   * @param matchDate - ISO date string or null from the API
   */
  formatMatchDate(matchDate: string | null): string {
    if (!matchDate) {
      return this.translate.instant('na');
    }
    return new Date(matchDate).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  /** Returns victory points including manual adjustment for a category. */
  getVictoryPoints(category: RatingCategoryBreakdown): number {
    return (category.victory_points ?? 0) + (category.manual_vp_adjustment ?? 0);
  }

  /** Returns bonus points including manual adjustment for a category. */
  getBonusPoints(category: RatingCategoryBreakdown): number {
    return (category.bonus_points ?? 0) + (category.manual_bp_adjustment ?? 0);
  }

  /** Returns formatted penalty display value for a category. */
  getPenaltyDisplay(category: RatingCategoryBreakdown): string {
    const penalty = category.penalty_points ?? 0;
    return penalty === 0 ? '-0' : `-${Math.abs(penalty)}`;
  }

  /** Whether tournament weight should be shown for a category. */
  shouldShowTournamentWeight(category: RatingCategoryBreakdown): boolean {
    return category.tournament_weight != null && category.tournament_weight !== 1;
  }

  /**
   * Formats the API last_updated_at timestamp for display.
   * @param isoDate - ISO date string from the API
   */
  formatLastUpdated(isoDate: string): string {
    if (!isoDate) {
      return '';
    }
    return new Date(isoDate).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  /** Maps event.details into level-1 display rows. */
  private mapDetailsToDisplay(details: EventBreakdownDetails, isHeld: boolean): ComponentSummary[] {
    const mapped: ComponentSummary[] = [];

    const victoryPoints = (details.victory_points ?? 0) + (details.manual_vp_adjustment ?? 0);
    if (victoryPoints !== 0 || isHeld) {
      mapped.push({
        heading: 'Victory Points',
        labelKey: 'victory_points',
        points: victoryPoints,
        isTournamentWeight: false,
      });
    }

    const bonusPoints = (details.bonus_points ?? 0) + (details.manual_bp_adjustment ?? 0);
    if (bonusPoints !== 0 || isHeld) {
      mapped.push({
        heading: 'Bonus Points',
        labelKey: 'bonus_points',
        points: bonusPoints,
        isTournamentWeight: false,
      });
    }

    if (details.tournament_weight != null && details.tournament_weight !== 1) {
      mapped.push({
        heading: 'Tournament Weight',
        labelKey: 'tournament_weight',
        points: details.tournament_weight,
        isTournamentWeight: true,
      });
    }

    if (details.penalty_points != null && details.penalty_points !== 0) {
      mapped.push({
        heading: 'Penalty Points',
        labelKey: 'penalty_points',
        points: -Math.abs(details.penalty_points),
        isTournamentWeight: false,
      });
    }

    return mapped;
  }

  /** Clears expanded breakdown UI state. */
  private resetBreakdownState(): void {
    this.selectedBreakdownEventId = null;
    this.mappedBreakdownDetails = [];
    this.closeCategorySidebar();
    this.cdr.markForCheck();
  }

  showHoldTip(event: MouseEvent, reason: string | null | undefined): void {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const left = Math.min(
      Math.max(rect.left + rect.width / 2 - HOLD_TIP_WIDTH / 2, HOLD_TIP_MARGIN),
      window.innerWidth - HOLD_TIP_WIDTH - HOLD_TIP_MARGIN
    );
    this.holdTip = { text: reason ?? '', top: rect.bottom + 8, left };
  }

  hideHoldTip(): void {
    this.holdTip = null;
  }

  onClose(): void {
    if (this.isMatchSidebarOpen) {
      this.closeMatchSidebar();
      return;
    }
    if (this.isCategorySidebarOpen) {
      this.closeCategorySidebar();
      return;
    }
    this.panelClose.emit();
  }

  trackByEventId(_index: number, event: EventPerformance): number {
    return event.event_id;
  }

  trackByLabelKey(_index: number, item: ComponentSummary): string {
    return item.labelKey + item.heading;
  }

  trackByCategoryId(_index: number, category: RatingCategoryBreakdown): number {
    return category.category_id;
  }

  trackByMatchId(_index: number, match: { match_id: number }): number {
    return match.match_id;
  }

  trackByMatchGroupIndex(index: number): number {
    return index;
  }
}
